import { gsap } from 'gsap'

import { Draggable } from 'gsap/Draggable'

gsap.registerPlugin(Draggable)

export function initStackedCardsSlider(root = document) {
    root
        .querySelectorAll('[data-stacked-cards]')
        .forEach(function (container) {
            if (container.dataset.stackedCardsInitialized === 'true') return
            container.dataset.stackedCardsInitialized = 'true'

            // animation presets
            let easeBeforeRelease = { duration: 0.2, ease: 'Power2.easeOut' }
            let easeAfterRelease = { duration: 1, ease: 'elastic.out(1,0.75)' }

            let activeDeg = 2
            let inactiveDeg = -2

            const list = container.querySelector('[data-stacked-cards-list]')
            if (!list) return

            // check minimum cards
            const initialItems = Array.from(
                list.querySelectorAll(':scope > [data-stacked-cards-item]')
            )
            if (initialItems.length < 3) {
                console.error(
                    '[StackedCards] Minimum of 3 cards required. Found:',
                    initialItems.length,
                    list
                )
                return
            }

            // Draggable instances & cached elements
            let dragFirst, dragSecond
            let firstItem, secondItem, firstEl, secondEl
            let full, t
            let isAnimating = false

            function restack() {
                const items = Array.from(
                    list.querySelectorAll('[data-stacked-cards-item]')
                )
                items.forEach(function (item) {
                    item.classList.remove('is--active', 'is--second')
                })
                items[0].style.zIndex = 3
                items[0].style.transform = `rotate(${activeDeg}deg)`
                items[0].style.pointerEvents = 'auto'
                items[0].classList.add('is--active')

                items[1].style.zIndex = 2
                items[1].style.transform = `rotate(${inactiveDeg}deg)`
                items[1].style.pointerEvents = 'none'
                items[1].classList.add('is--second')

                items[2].style.zIndex = 1
                items[2].style.transform = `rotate(${activeDeg}deg)`

                items.slice(3).forEach(function (item) {
                    item.style.zIndex = 0
                    item.style.transform = `rotate(${inactiveDeg}deg)`
                })
            }

            function setupDraggables() {
                restack()

                // cache top two cards
                const items = Array.from(
                    list.querySelectorAll(':scope > [data-stacked-cards-item]')
                )
                firstItem = items[0]
                secondItem = items[1]
                firstEl = firstItem.querySelector('[data-stacked-cards-card]')
                secondEl = secondItem.querySelector('[data-stacked-cards-card]')

                // compute thresholds
                const width = firstEl.getBoundingClientRect().width
                full = width * 1.15
                t = width * 0.1

                // kill old Draggables
                dragFirst?.kill()
                dragSecond?.kill()

                // --- First card draggable ---
                dragFirst = Draggable.create(firstEl, {
                    type: 'x',
                    onPress() {
                        firstEl.classList.add('is--dragging')
                    },
                    onRelease() {
                        firstEl.classList.remove('is--dragging')
                    },
                    onDrag() {
                        let raw = this.x
                        if (Math.abs(raw) > full) {
                            const over = Math.abs(raw) - full
                            raw = (raw > 0 ? 1 : -1) * (full + over * 0.1)
                        }
                        gsap.set(firstEl, { x: raw, rotation: 0 })
                    },
                    onDragEnd() {
                        const x = this.x
                        const dir = x > 0 ? 'right' : 'left'

                        // hand control to second card
                        this.disable?.()
                        dragSecond?.enable?.()
                        firstItem.style.pointerEvents = 'none'
                        secondItem.style.pointerEvents = 'auto'

                        if (Math.abs(x) <= t) {
                            // small drag: just snap back
                            isAnimating = true
                            gsap.to(firstEl, {
                                x: 0,
                                rotation: 0,
                                ...easeBeforeRelease,
                                onComplete: resetCycle
                            })
                        } else if (Math.abs(x) <= full) {
                            flick(dir, false, x)
                        } else {
                            flick(dir, true)
                        }
                    }
                })[0]

                // --- Second card draggable ---
                dragSecond = Draggable.create(secondEl, {
                    type: 'x',
                    onPress() {
                        secondEl.classList.add('is--dragging')
                    },
                    onRelease() {
                        secondEl.classList.remove('is--dragging')
                    },
                    onDrag() {
                        let raw = this.x
                        if (Math.abs(raw) > full) {
                            const over = Math.abs(raw) - full
                            raw = (raw > 0 ? 1 : -1) * (full + over * 0.2)
                        }
                        gsap.set(secondEl, { x: raw, rotation: 0 })
                    },
                    onDragEnd() {
                        gsap.to(secondEl, {
                            x: 0,
                            rotation: 0,
                            ...easeBeforeRelease
                        })
                    }
                })[0]

                // start with first card active
                dragFirst?.enable?.()
                dragSecond?.disable?.()
                firstItem.style.pointerEvents = 'auto'
                secondItem.style.pointerEvents = 'none'
            }

            function flick(dir, skipHome = false, releaseX = 0) {
                if (isAnimating) return
                isAnimating = true

                if (!(dir === 'left' || dir === 'right')) {
                    dir = activeDeg > 0 ? 'right' : 'left'
                }
                dragFirst?.disable?.()

                const item = list.querySelector('[data-stacked-cards-item]')
                const card = item.querySelector('[data-stacked-cards-card]')
                const exitX = dir === 'right' ? full : -full

                if (skipHome) {
                    gsap.killTweensOf(card)
                    const visualX = gsap.getProperty(card, 'x')
                    list.appendChild(item)
                    ;[activeDeg, inactiveDeg] = [inactiveDeg, activeDeg]
                    restack()
                    gsap.fromTo(
                        card,
                        { x: visualX, rotation: 0 },
                        {
                            x: 0,
                            rotation: 0,
                            ...easeAfterRelease,
                            onComplete: resetCycle
                        }
                    )
                } else {
                    gsap.fromTo(
                        card,
                        { x: releaseX, rotation: 0 },
                        {
                            x: exitX,
                            ...easeBeforeRelease,
                            onComplete() {
                                gsap.killTweensOf(card)
                                gsap.set(card, { x: 0, rotation: 0 })
                                list.appendChild(item)
                                ;[activeDeg, inactiveDeg] = [
                                    inactiveDeg,
                                    activeDeg
                                ]
                                restack()
                                const newCard = item.querySelector(
                                    '[data-stacked-cards-card]'
                                )
                                gsap.fromTo(
                                    newCard,
                                    { x: exitX },
                                    {
                                        x: 0,
                                        ...easeAfterRelease,
                                        onComplete: resetCycle
                                    }
                                )
                            }
                        }
                    )
                }
            }

            function previous(dir = 'left') {
                if (isAnimating) return
                isAnimating = true
                dragFirst?.disable?.()
                dragSecond?.disable?.()

                const items = Array.from(
                    list.querySelectorAll(':scope > [data-stacked-cards-item]')
                )
                const item = items[items.length - 1]
                const card = item.querySelector('[data-stacked-cards-card]')
                const exitX = dir === 'right' ? full : -full

                gsap.killTweensOf(card)
                gsap.fromTo(
                    card,
                    { x: 0, rotation: 0 },
                    {
                        x: exitX,
                        ...easeBeforeRelease,
                        onComplete() {
                            gsap.killTweensOf(card)
                            gsap.set(card, { x: exitX, rotation: 0 })
                            list.insertBefore(item, items[0])
                            ;[activeDeg, inactiveDeg] = [
                                inactiveDeg,
                                activeDeg
                            ]
                            restack()
                            gsap.to(card, {
                                x: 0,
                                rotation: 0,
                                ...easeAfterRelease,
                                onComplete: resetCycle
                            })
                        }
                    }
                )
            }

            function resetCycle() {
                list.querySelectorAll(
                    '[data-stacked-cards-card].is--dragging'
                ).forEach(function (el) {
                    el.classList.remove('is--dragging')
                })
                setupDraggables()
                isAnimating = false
            }

            setupDraggables()

            const controlsScope = container.closest('.relative') || container

            controlsScope
                .querySelectorAll(
                    '[data-next], [data-stacked-cards-control="next"]'
                )
                .forEach(function (btn) {
                    btn.addEventListener('click', function () {
                        flick('right')
                    })
                })

            controlsScope
                .querySelectorAll(
                    '[data-prev], [data-stacked-cards-control="prev"]'
                )
                .forEach(function (btn) {
                    btn.addEventListener('click', function () {
                        previous('left')
                    })
                })
        })
}
